/* =============================================================
   NAVIGATION CONFIG
   =============================================================
   The left sidebar is generated from this file — adding a section
   is a matter of adding an entry, not editing the Sidebar component.

   `roles` is optional: leave it out and the item shows for everyone.
   When present, the item is only rendered for the listed account
   types (`both` sees everything).
   ============================================================= */

import type { IconName } from '@/components/icons'
import type { UserRole } from '@/lib/auth/types'
import { routes } from './routes'

export interface NavItem {
  label: string
  to: string
  icon: IconName
  /** Restrict to certain account types. Omit to show for all. */
  roles?: UserRole[]
  /** Small pill after the label, e.g. "Soon". */
  badge?: string
  /** Also mark active when the URL is a child of `to` (default: true). */
  matchChildren?: boolean
}

export interface NavGroup {
  /** Small caps heading above the group. Omit for an unlabelled group. */
  label?: string
  items: NavItem[]
}

/** Main navigation, rendered top-down in the sidebar body. */
export const navGroups: NavGroup[] = [
  {
    items: [{ label: 'Dashboard', to: routes.dashboard, icon: 'dashboard' }],
  },
  {
    label: 'Infrastructure',
    items: [
      {
        label: 'Docking locations',
        to: routes.locations,
        icon: 'anchor',
        roles: ['infrastructure_owner', 'both'],
      },
      {
        label: 'Availability',
        to: routes.availability,
        icon: 'calendar',
        roles: ['infrastructure_owner', 'both'],
      },
    ],
  },
  {
    label: 'Fleet',
    items: [
      {
        label: 'Vessels',
        to: routes.vessels,
        icon: 'ship',
        roles: ['shipowner', 'both'],
      },
      {
        label: 'Find a berth',
        to: routes.search,
        icon: 'search',
        roles: ['shipowner', 'both'],
      },
    ],
  },
  {
    label: 'Operations',
    items: [
      { label: 'Bookings', to: routes.bookings, icon: 'bookings' },
      { label: 'Reports', to: routes.reports, icon: 'chart', badge: 'Soon' },
    ],
  },
]

/** Pinned to the bottom of the sidebar, below the scrolling area. */
export const navFooterItems: NavItem[] = [
  { label: 'Settings', to: routes.settings, icon: 'settings' },
]

/** True if a nav item should be visible to the given account type. */
export function isNavItemVisible(item: NavItem, role: UserRole | undefined): boolean {
  if (!item.roles) return true
  if (!role) return false
  return role === 'both' || item.roles.includes(role)
}

/** Every nav item, flattened — used for reverse lookups. */
export const allNavItems: NavItem[] = [...navGroups.flatMap((group) => group.items), ...navFooterItems]

/**
 * The nav item a URL belongs to, longest match first so `/locations/42`
 * resolves to "Docking locations". Lets the top bar label itself from
 * the same config the sidebar is built from.
 */
export function findNavItemByPath(pathname: string): NavItem | undefined {
  return [...allNavItems]
    .sort((a, b) => b.to.length - a.to.length)
    .find((item) => pathname === item.to || pathname.startsWith(`${item.to}/`))
}
